import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import EzTable from "../../../common/EzTable";
import { EzToast, TOAST_SEVERITY } from "../../../common/EzToast";
import EzNotFound from "../../../common/EzNotFound";
import { Box, Container, Grid } from "@mui/material";
import Breadcrumb from "../../../common/Breadcrumb";
import { Button } from "primereact/button";
import { useStyles } from "./style";
import EzControlledCalendarWithFloatingLabel from "../../../common/EzForm/EzControlledCalendar/WithFloatingLabel";
import EzControlledMultiSelecttWithFloatingLabel from "../../../common/EzForm/EzControlledMultiSelect/WithFloatingLabel";
import EzControlledDropDowntWithFloatingLabel from "../../../common/EzForm/EzControlledDropDown/WithFloatingLabel";
import { getFormBaseValidationRule } from "../../../utils/form/form-utils";
import { formatDate, DATE_STRATEGY } from "../../../utils/date";
import { reservationStatusDD, reportTypeDD } from "./constant";

const getIconPath = (imgPathDelta = "/no-record.png") =>
  process.env.PUBLIC_URL + imgPathDelta;

const AffiliateReport = (props) => {
  const classes = useStyles();
  const {
    fetchAffiliateReport,
    tableData,
    downloadAffiliateReport,
    isPageLevelError,
    paginationInfo,
    isLoading,
    screenPermissions,
    affiliateDD,
    affiliateDependentLocationsDD,
    affiliateDependentNetworksDD,
    isLoadingAffiliateDependentDD,
    fetchAffiliateDependentLocationsDD,
    fetchAffiliateDependentNetworksDD,
    clearAffiliateDependentDropdowns,
    reportFilters,
    setReportFilters,
    clearReportFilters,
    tableColumnFilters,
    setTableColumnFilters,
    clearTableColumnFilters,
    savedTableState,
    onTableStateChange,
    affiliateReportSummary,
  } = props;

  // Convert string dates to Date objects for the form
  const convertFiltersForForm = (filters) => {
    if (!filters) return filters;
    
    const converted = { ...filters };
    
    // Convert string dates to local Date objects
    if (converted.startDate && typeof converted.startDate === 'string') {
      const [year, month, day] = converted.startDate.split('-');
      converted.startDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
    
    if (converted.endDate && typeof converted.endDate === 'string') {
      const [year, month, day] = converted.endDate.split('-');
      converted.endDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
    
    return converted;
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: convertFiltersForForm(reportFilters),
  });

  useEffect(() => {
    reset(convertFiltersForForm(reportFilters));
  }, [reportFilters, reset]);

  const selectedAffiliateIds = watch("affiliateId");
  const [previousAffiliateIds, setPreviousAffiliateIds] = useState([]);

  useEffect(() => {
    const currentIds = selectedAffiliateIds
      ? selectedAffiliateIds.sort().join(",")
      : "";
    const prevIds = previousAffiliateIds
      ? previousAffiliateIds.sort().join(",")
      : "";

    if (currentIds !== prevIds) {
      if (selectedAffiliateIds && selectedAffiliateIds.length > 0) {
        const affiliateIdsString = selectedAffiliateIds.join(",");
        fetchAffiliateDependentLocationsDD(affiliateIdsString);
        fetchAffiliateDependentNetworksDD(affiliateIdsString);
      } else {
        clearAffiliateDependentDropdowns();
        setValue("locationId", []);
        setValue("networkId", []);
      }
      setPreviousAffiliateIds(selectedAffiliateIds || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAffiliateIds]);

  const [hasSearched, setHasSearched] = useState(() => {
    return (
      reportFilters &&
      (reportFilters.startDate ||
        reportFilters.endDate ||
        reportFilters.locationId?.length > 0 ||
        reportFilters.networkId?.length > 0 ||
        reportFilters.affiliateId?.length > 0 ||
        reportFilters.status ||
        reportFilters.type)
    );
  });

  const [areFiltersVisible, toggleFilterState] = useState(true);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [downloadError, setDownloadError] = useState(false);

  const breadcrumbD = [
    { path: "/dashboard", label: "Dashboard" },
    { path: "/affiliate", label: "Affiliate" },
    { path: "/affiliate-report", label: "Affiliate Report" },
  ];

  const getFormErrorMessage = (name) => {
    return (
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  useEffect(() => {
    if (downloadSuccess) {
      const timer = setTimeout(() => {
        setDownloadSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [downloadSuccess]);

  useEffect(() => {
    if (downloadError) {
      const timer = setTimeout(() => {
        setDownloadError(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [downloadError]);

  const onSubmit = (data) => {
    const filters = {
      startDate: data.startDate ? formatDate(data.startDate, DATE_STRATEGY.DATE_ISO) : null,
      endDate: data.endDate ? formatDate(data.endDate, DATE_STRATEGY.DATE_ISO) : null,
      locationId: data.locationId || [],
      networkId: data.networkId || [],
      affiliateId: data.affiliateId || [],
      status: data.status ?? null,
      type: data.type || null,
    };

    setReportFilters(filters);
    setHasSearched(true);

    fetchAffiliateReport({
      pageNumber: 0,
      pageSize: 10,
      sortField: "id",
      sortOrder: -1,
      additinalFilter: filters,
    });
  };

  const handleClearFilters = () => {
    reset({
      startDate: null,
      endDate: null,
      locationId: [],
      networkId: [],
      affiliateId: [],
      status: null,
      type: null,
    });
    clearReportFilters();
    clearTableColumnFilters();
    setHasSearched(false);
  };

  const handleDownloadReport = async () => {
    const currentFilters = watch();
    const payload = {
      startDate: currentFilters.startDate ? formatDate(currentFilters.startDate, DATE_STRATEGY.DATE_ISO) : null,
      endDate: currentFilters.endDate ? formatDate(currentFilters.endDate, DATE_STRATEGY.DATE_ISO) : null,
      locationId: currentFilters.locationId || [],
      networkId: currentFilters.networkId || [],
      affiliateId: currentFilters.affiliateId || [],
      status: currentFilters.status ?? null,
      type: currentFilters.type || null,
    };

    try {
      await downloadAffiliateReport(payload);
      setDownloadSuccess(true);
    } catch (error) {
      setDownloadError(true);
    }
  };

  const renderPageHeaderSection = () => {
    return (
      <div className="make-relative sub-header">
        <div className="maintitlediv">
          <div className="ml-3">
            <h4 className="pagetitle">Affiliate Report</h4>
            <Button
              label={areFiltersVisible ? "Hide Search" : "Show Search"}
              icon={areFiltersVisible ? "pi pi-filter-slash" : "pi pi-filter"}
              className={`p-button-text p-button-secondary shadow-btn`}
              onClick={() => {
                toggleFilterState(!areFiltersVisible);
              }}
            />
          </div>
          <div className="buttonarea">
            <Button
              label="Download Affiliate Report"
              icon="pi pi-download"
              className="p-button-raised"
              onClick={handleDownloadReport}
              disabled={!hasSearched || isLoading}
              loading={isLoading}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderSearchForm = () => {
    return (
      <div className="form-demo">
        <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <EzControlledCalendarWithFloatingLabel
                name="startDate"
                control={control}
                label="Start Date"
                maxDate={new Date(2090, 0, 1)}
                rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                isError={errors.startDate}
                errorMsg={getFormErrorMessage("startDate")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <EzControlledCalendarWithFloatingLabel
                name="endDate"
                control={control}
                label="End Date"
                maxDate={new Date(2090, 0, 1)}
                rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                isError={errors.endDate}
                errorMsg={getFormErrorMessage("endDate")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <EzControlledMultiSelecttWithFloatingLabel
                name="affiliateId"
                control={control}
                rules={getFormBaseValidationRule().DEFAULT.DO_NOT_VALIDATE}
                optionLabel={"name"}
                optionValue={"id"}
                options={affiliateDD || []}
                isError={errors["affiliateId"]}
                errorMsg={getFormErrorMessage("affiliateId")}
                label="Affiliate"
                labelClassName={"ml-2"}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <EzControlledMultiSelecttWithFloatingLabel
                name="locationId"
                control={control}
                rules={getFormBaseValidationRule().DEFAULT.DO_NOT_VALIDATE}
                optionLabel={"name"}
                optionValue={"id"}
                options={affiliateDependentLocationsDD || []}
                isError={errors["locationId"]}
                errorMsg={getFormErrorMessage("locationId")}
                label="Location"
                labelClassName={"ml-2"}
                loading={isLoadingAffiliateDependentDD}
                disabled={
                  !selectedAffiliateIds || selectedAffiliateIds.length === 0
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <EzControlledMultiSelecttWithFloatingLabel
                name="networkId"
                control={control}
                rules={getFormBaseValidationRule().DEFAULT.DO_NOT_VALIDATE}
                optionLabel={"name"}
                optionValue={"id"}
                options={affiliateDependentNetworksDD || []}
                isError={errors["networkId"]}
                errorMsg={getFormErrorMessage("networkId")}
                label="Network"
                labelClassName={"ml-2"}
                loading={isLoadingAffiliateDependentDD}
                disabled={
                  !selectedAffiliateIds || selectedAffiliateIds.length === 0
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <EzControlledDropDowntWithFloatingLabel
                name="type"
                control={control}
                rules={getFormBaseValidationRule().DEFAULT.DO_NOT_VALIDATE}
                optionLabel={"name"}
                optionValue={"id"}
                options={reportTypeDD}
                isError={errors["type"]}
                errorMsg={getFormErrorMessage("type")}
                label="Type"
                labelClassName={"ml-2"}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <EzControlledDropDowntWithFloatingLabel
                name="status"
                control={control}
                rules={getFormBaseValidationRule().DEFAULT.DO_NOT_VALIDATE}
                optionLabel={"name"}
                optionValue={"id"}
                options={reservationStatusDD}
                isError={errors["status"]}
                errorMsg={getFormErrorMessage("status")}
                label="Status"
                labelClassName={"ml-2"}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={8} lg={3} mb={2}>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  margin: "0px 0px 0px 10px",
                }}
              >
                <Button
                  type="submit"
                  label="Search"
                  className="p-button-raised"
                />
                <Button
                  type="button"
                  label="Reset Filters"
                  className="p-button-raised p-button-secondary"
                  onClick={handleClearFilters}
                />
              </div>
            </Grid>
          </Grid>
        </form>
      </div>
    );
  };

  const shouldRenderFullPageError = () => isPageLevelError;
  const shouldRenderRecords = () =>
    hasSearched &&
    tableData?.columns?.length > 0 &&
    tableData?.rows?.length > 0;
  const shouldRenderNotFoundView = () =>
    hasSearched && !shouldRenderRecords() && !shouldRenderFullPageError();
  const shouldRenderInitialView = () => !hasSearched;

  // Memoize filters to prevent unnecessary re-renders and ensure stable reference
  const filters = React.useMemo(() => {
    const getFilterValue = (key) => tableColumnFilters?.[key] ?? null;
    return {
      global: { value: null },
      ref: { value: getFilterValue("ref"), filterKey: "ref" },
      affiliateName: { value: getFilterValue("affiliateName"), filterKey: "affiliateName" },
      pickupDate: { value: getFilterValue("pickupDate"), filterKey: "pickupDate" },
      pickupTime: { value: getFilterValue("pickupTime"), filterKey: "pickupTime" },
      dropoffDate: { value: getFilterValue("dropoffDate"), filterKey: "dropoffDate" },
      dropoffTime: { value: getFilterValue("dropoffTime"), filterKey: "dropoffTime" },
      totalAmount: { value: getFilterValue("totalAmount"), filterKey: "totalAmount" },
      reservationStatus: { value: getFilterValue("reservationStatus"), filterKey: "reservationStatus" },
      createdTime: { value: getFilterValue("createdTime"), filterKey: "createdTime" },
      locationName: { value: getFilterValue("locationName"), filterKey: "locationName" },
      networkName: { value: getFilterValue("networkName"), filterKey: "networkName" },
      renterName: { value: getFilterValue("renterName"), filterKey: "renterName" },
      renterEmail: { value: getFilterValue("renterEmail"), filterKey: "renterEmail" },
      commissionAmount: { value: getFilterValue("commissionAmount"), filterKey: "commissionAmount" },
    };
  }, [tableColumnFilters]);

  const handleTableFilterChange = (newFilters) => {
    const filterValues = {};
    Object.keys(newFilters).forEach(key => {
      if (key !== 'global' && newFilters[key]?.value !== null && newFilters[key]?.value !== undefined && newFilters[key]?.value !== "") {
        filterValues[key] = newFilters[key].value;
      }
    });
    setTableColumnFilters(filterValues);
  };

  const getToastProps = () => {
    if (downloadSuccess) {
      return {
        severity: TOAST_SEVERITY.SUCCESS,
        toastTitle: "Affiliate report downloaded successfully!",
        shouldShowToast: true,
      };
    }

    if (downloadError) {
      return {
        severity: TOAST_SEVERITY.ERROR,
        toastTitle: "Failed to download affiliate report. Please try again.",
        shouldShowToast: true,
      };
    }

    return {};
  };

  const tableProps = {
    areFiltersVisible: false,
    columns: tableData?.columns,
    paginationInfo,
    screenPermissions,
    value: tableData?.rows,
    emptyMessage: "No records found.",
    filters,
    loadLazyData: fetchAffiliateReport,
    loading: isLoading,
    showMoreActions: false,
    sortField: savedTableState?.sortField || "id",
    initialSortOrder: savedTableState?.sortOrder || -1,
    initialFirst: savedTableState?.first || 0,
    initialRows: savedTableState?.rows || 10,
    onTableStateChange,
    onTableFilterChange: handleTableFilterChange,
    setDeleteConfirmationPopupVisibility: () => {},
    showViewButton: false,
    showCopyButton: false,
    showDeleteButton: false,
    showEditButton: false,
    additinalFilter: reportFilters,
  };

  return (
    <>
      <Container maxWidth="xl">
        <Breadcrumb list={breadcrumbD} />
        <EzToast {...getToastProps()} />
        <div className={`containerbox ${classes.bg}`}>
          {renderPageHeaderSection()}
          {areFiltersVisible && (
            <div className="search-form-container">{renderSearchForm()}</div>
          )}
          <div className="mTop15">
            {shouldRenderFullPageError() && (
              <EzNotFound
                messageStyle={{ marginLeft: "-70px" }}
                bgImgUrl={getIconPath("something-broke.png")}
                message={"Oops! Something broke, we are working on a fix."}
              />
            )}
            {shouldRenderInitialView() && (
              <EzNotFound
                bgImgUrl={getIconPath()}
                message={
                  "Please select filters and click Search to view report"
                }
              />
            )}
            {shouldRenderRecords() && (
              <Box>
                <EzTable {...tableProps} />
              </Box>
            )}
            {shouldRenderNotFoundView() && (
              <EzNotFound
                bgImgUrl={getIconPath()}
                message={"No records found for the selected filters"}
              />
            )}
          </div>

          {affiliateReportSummary && hasSearched && (
            <div
              className="affiliate-report-summary-section"
              style={{ marginTop: "20px", marginBottom: "20px" }}
            >
              <h4
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  marginBottom: "15px",
                  color: "#333",
                  borderBottom: "2px solid #1976d2",
                  paddingBottom: "8px",
                }}
              >
                Affiliate Report Summary
              </h4>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "15px",
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <tbody>
                        <tr>
                          <td
                            style={{
                              padding: "8px 0",
                              fontWeight: "500",
                              color: "#555",
                            }}
                          >
                            Cancelled Reservation Count
                          </td>
                          <td
                            style={{
                              padding: "8px 0",
                              textAlign: "right",
                              fontWeight: "600",
                            }}
                          >
                            {affiliateReportSummary.cancelledReservationCount ||
                              0}
                          </td>
                        </tr>
                        <tr>
                          <td
                            style={{
                              padding: "8px 0",
                              fontWeight: "500",
                              color: "#555",
                            }}
                          >
                            Cancelled Reservation Amount
                          </td>
                          <td
                            style={{
                              padding: "8px 0",
                              textAlign: "right",
                              fontWeight: "600",
                            }}
                          >
                            ${" "}
                            {typeof affiliateReportSummary.cancelledReservationAmount ===
                            "number"
                              ? affiliateReportSummary.cancelledReservationAmount.toFixed(
                                  2,
                                )
                              : affiliateReportSummary.cancelledReservationAmount ||
                                "0.00"}
                          </td>
                        </tr>
                        <tr>
                          <td
                            style={{
                              padding: "8px 0",
                              fontWeight: "500",
                              color: "#555",
                            }}
                          >
                            Confirmed Reservation Count
                          </td>
                          <td
                            style={{
                              padding: "8px 0",
                              textAlign: "right",
                              fontWeight: "600",
                            }}
                          >
                            {affiliateReportSummary.confirmedReservationCount ||
                              0}
                          </td>
                        </tr>
                        <tr>
                          <td
                            style={{
                              padding: "8px 0",
                              fontWeight: "500",
                              color: "#555",
                            }}
                          >
                            Confirmed Reservation Amount
                          </td>
                          <td
                            style={{
                              padding: "8px 0",
                              textAlign: "right",
                              fontWeight: "600",
                            }}
                          >
                            ${" "}
                            {typeof affiliateReportSummary.confirmedReservationAmount ===
                            "number"
                              ? affiliateReportSummary.confirmedReservationAmount.toFixed(
                                  2,
                                )
                              : affiliateReportSummary.confirmedReservationAmount ||
                                "0.00"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Grid>
                <Grid item xs={12} md={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "15px",
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <tbody>
                        <tr>
                          <td
                            style={{
                              padding: "8px 0",
                              fontWeight: "500",
                              color: "#555",
                            }}
                          >
                            No Show Reservation Count
                          </td>
                          <td
                            style={{
                              padding: "8px 0",
                              textAlign: "right",
                              fontWeight: "600",
                            }}
                          >
                            {affiliateReportSummary.noshowReservationCount || 0}
                          </td>
                        </tr>
                        <tr>
                          <td
                            style={{
                              padding: "8px 0",
                              fontWeight: "500",
                              color: "#555",
                            }}
                          >
                            No Show Reservation Amount
                          </td>
                          <td
                            style={{
                              padding: "8px 0",
                              textAlign: "right",
                              fontWeight: "600",
                            }}
                          >
                            ${" "}
                            {typeof affiliateReportSummary.noshowReservationAmount ===
                            "number"
                              ? affiliateReportSummary.noshowReservationAmount.toFixed(
                                  2,
                                )
                              : affiliateReportSummary.noshowReservationAmount ||
                                "0.00"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Grid>

                <Grid item xs={12} md={4}>
                  <div
                    style={{
                      backgroundColor: "#e8f5e9",
                      padding: "15px",
                      borderRadius: "8px",
                      border: "1px solid #c8e6c9",
                    }}
                  >
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <tbody>
                        <tr>
                          <td
                            style={{
                              padding: "8px 0",
                              fontWeight: "500",
                              color: "#2e7d32",
                            }}
                          >
                            Total Commission Amount
                          </td>
                          <td
                            style={{
                              padding: "8px 0",
                              textAlign: "right",
                              fontWeight: "700",
                              color: "#2e7d32",
                              fontSize: "16px",
                            }}
                          >
                            ${" "}
                            {typeof affiliateReportSummary.totalCommissionAmount ===
                            "number"
                              ? affiliateReportSummary.totalCommissionAmount.toFixed(
                                  2,
                                )
                              : affiliateReportSummary.totalCommissionAmount ||
                                "0.00"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Grid>
              </Grid>
            </div>
          )}
        </div>
      </Container>
    </>
  );
};

export default AffiliateReport;
